import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, MailCheck, Church } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Forgot-password entry screen. Users type their email (or phone) plus
 * the church code that scopes the lookup. The backend always returns a
 * generic 200 whether or not a match exists — that stops attackers from
 * enumerating live email addresses.
 */
export default function ForgotPassword() {
  const [params] = useSearchParams();
  const isSuper = params.get("super") === "1";
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [defaultChurch, setDefaultChurch] = useState(null);
  const [defaultLoading, setDefaultLoading] = useState(true);

  // Match the login page — hide the church code when a single-tenant
  // default church exists.
  useEffect(() => {
    if (isSuper) { setDefaultLoading(false); return; }
    axios.get(`${API}/public/default-church`)
      .then((r) => {
        setDefaultChurch(r.data);
        if (r.data?.church_code) setCode(r.data.church_code);
      })
      .catch(() => setDefaultChurch(null))
      .finally(() => setDefaultLoading(false));
  }, [isSuper]);

  const hideCodeField = !isSuper && Boolean(defaultChurch?.church_code);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { identifier: identifier.trim() };
      if (!isSuper) payload.church_code = code.trim().toUpperCase();
      await axios.post(`${API}/auth/forgot-password`, payload);
      setSent(true);
    } catch (err) {
      // Backend never actually fails here, but guard anyway.
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="mobile-shell px-5 pt-10 min-h-screen" data-testid="forgot-password-sent">
        <div className="max-w-md mx-auto text-center">
          <div className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-4"
               style={{ backgroundColor: "color-mix(in srgb, var(--tenant-primary) 12%, transparent)" }}>
            <MailCheck className="w-7 h-7 tenant-text" strokeWidth={1.8} />
          </div>
          <h1 className="font-serif-display text-2xl">Check your email</h1>
          <p className="text-sm text-stone-500 mt-2 leading-relaxed">
            If an account matches, we&apos;ve sent a reset link to your email. The link is good for the next hour.
          </p>
          <p className="text-xs text-stone-400 mt-4">
            Didn&apos;t get it? Check spam, or ask your main admin to reset your password from Admin → Members.
          </p>
          <Link
            to="/login"
            data-testid="forgot-password-back-to-login"
            className="mt-8 inline-flex items-center gap-1 text-sm tenant-text font-semibold"
          >
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-shell px-5 pt-6 min-h-screen" data-testid="forgot-password">
      <Link
        to="/login"
        data-testid="forgot-password-back"
        className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-700"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Sign in
      </Link>
      <h1 className="font-serif-display text-2xl mt-4">Forgot password</h1>
      <p className="text-sm text-stone-500 mt-1">
        {isSuper
          ? "Enter your Super Admin email and we'll send a reset link."
          : hideCodeField
          ? "Enter your email (or phone number) and we'll send a reset link."
          : "Enter your email (or phone number) and the church code, and we'll send a reset link."}
      </p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        {!isSuper && !hideCodeField && !defaultLoading && (
          <div>
            <Label className="text-xs">Church code</Label>
            <Input
              data-testid="forgot-password-church-code"
              placeholder=""
              autoCapitalize="characters"
              required
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="bg-white tracking-widest font-semibold"
              maxLength={20}
            />
          </div>
        )}
        <div>
          <Label className="text-xs">Email or phone</Label>
          <Input
            data-testid="forgot-password-identifier"
            required
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            className="bg-white"
          />
        </div>
        <Button
          type="submit"
          disabled={loading || !identifier.trim() || (!isSuper && !code.trim())}
          data-testid="forgot-password-submit"
          className="w-full rounded-full text-white h-11"
          style={{ backgroundColor: isSuper ? "#0F172A" : "var(--tenant-primary)" }}
        >
          {loading ? "Sending…" : "Send reset link"}
        </Button>
      </form>

      <div className="mt-8 text-xs text-stone-400 leading-relaxed">
        <div className="font-semibold text-stone-500 mb-1">Can&apos;t access email?</div>
        Ask your main admin to reset your password from Admin → Members → Edit → set a new password. Your admin can hand you a fresh temporary password in-person.
      </div>
    </div>
  );
}
